// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject, TemplateRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { TEMPLATE_IDENTIFIERS } from '@lfx/key-projects/key-projects.model';

/**
 * Collaboration activity data model from SQL query (API returns uppercase keys)
 */
export interface CollaborationActivityData {
  PLATFORM: string;
  MEMBER_ID: string;
  MEMBER_DISPLAY_NAME: string;
  MEMBER_EMAIL: string;
  MEMBER_LOGO?: string;
  GITHUB_USERNAME?: string;
  LOCATION: string;
  ACTIVITY_COUNT: number;
  MOST_RECENT_ACTIVITY_DATE: string;
  MOST_RECENT_ACTIVITY_TS: string;
  ACCOUNT_ID: string;
  ACCOUNT_NAME: string;
  ACCOUNT_LOGO?: string;
  PROJECT_ID: string;
  PROJECT_NAME: string;
  PROJECT_SLUG: string;
}

/**
 * Collaboration activity summary data model
 */
export interface CollaborationActivitySummaryData {
  totalCollaborationActivities: number;
}

/**
 * Entity service for collaboration activity metric type
 */
@Injectable({
  providedIn: 'root'
})
export class CollaborationActivityMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  private readonly config = {
    definition: 'Non-code comments in repos, project documentation, and community messages. View all collaboration definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer" class="lf-link-v2">LFX Insights</a>.',
    columns: [
      { name: 'Source', prop: 'source' },
      { name: 'Our Collaborators', prop: 'displayName', templateId: TEMPLATE_IDENTIFIERS.USER_PROFILE },
      { name: 'Location', prop: 'location' },
      { name: 'Count', prop: 'count' },
      { name: 'Most recent', prop: 'mostRecent' }
    ],
    summaryColumns: [
      { name: 'Definition', prop: 'definition' },
      { name: 'Total count for this project', prop: 'totalCount' },
      { name: 'Data source', prop: 'dataSource' }
    ],
    dataSource: 'Confluence, Jira, GitHub, GitLab, Groups.io, Slack, Discord, Discourse'
  };

  /**
   * Get columns for collaboration activity table
   */
  getColumns(): Column[] {
    return this.config.columns.map(col => {
      const { templateId, ...columnProps } = col;
      return {
        ...columnProps,
        ...(templateId && { template: templateId as unknown as TemplateRef<unknown> })
      };
    });
  }

  /**
   * Get API endpoint for collaboration activity
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/collaboration-activity`;
  }

  /**
   * Fetch collaboration activity data from backend
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<CollaborationActivityData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch collaboration activity data');
            }

            return response.data.map(activity => this.transformToDataRow(activity));
          }),
          catchError(error => {
            console.error('Error fetching collaboration activity data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in collaboration activity entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform collaboration activity data to DataRow format for the table
   */
  private transformToDataRow(activity: CollaborationActivityData): DataRow {
    return {
      source: { text: activity.PLATFORM },
      displayName: {
        text: activity.MEMBER_DISPLAY_NAME,
        extraData: {
          imageUrl: activity.MEMBER_LOGO,
          userId: activity.MEMBER_ID,
          memberDisplayName: activity.MEMBER_DISPLAY_NAME,
          memberLogoUrl: activity.MEMBER_LOGO
        }
      },
      location: { text: activity.LOCATION },
      count: { text: activity.ACTIVITY_COUNT.toLocaleString('en-US') },
      mostRecent: { text: this.formatDate(activity.MOST_RECENT_ACTIVITY_DATE) }
    };
  }

  /**
   * Fetch collaboration activity summary data from backend
   * Summary data comes from ECOSYSTEM_INFLUENCE table (TOTAL_COLLABORATION_ACTIVITY field)
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: CollaborationActivitySummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch collaboration activity summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = this.config.summaryColumns;

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: { text: this.config.definition, isHtml: true },
                totalCount: { text: response.data.totalCollaborationActivities.toLocaleString('en-US') },
                dataSource: { text: this.config.dataSource }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching collaboration activity summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = this.config.summaryColumns;
            const fallbackData: DataRow[] = [
              {
                definition: { text: this.config.definition, isHtml: true },
                totalCount: { text: 'N/A' },
                dataSource: { text: this.config.dataSource }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in collaboration activity summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = this.config.summaryColumns;
        const fallbackData: DataRow[] = [
          {
            definition: { text: this.config.definition, isHtml: true },
            totalCount: { text: 'N/A' },
            dataSource: { text: this.config.dataSource }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }
}

